import React, {Component} from 'react';
import {
	Box,
	Button,
	Drawer,
	IconButton,
	Slider,
	Tab,
	Tabs,
	Tooltip,
	Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';
import TuneIcon from '@mui/icons-material/Tune';
import {store} from '../../../helper/store';
import {patch} from '../../../helper/request';
import {error_manager} from '../../../helper/error_manager';
import {updateMapState} from '../../../redux_actions/maps.state.action';

class RGBCurvesOverlay extends Component {
	static displayName = 'RGBCurvesOverlay';
	constructor(props) {
		super(props);
		this.state = {
			open: false,
			activeChannel: 'rgb',
			filterEnabled: true,
			curves: {
				rgb: [{x: 0, y: 0}, {x: 1, y: 1}],
				r: [{x: 0, y: 0}, {x: 1, y: 1}],
				g: [{x: 0, y: 0}, {x: 1, y: 1}],
				b: [{x: 0, y: 0}, {x: 1, y: 1}]
			},
			linearScales: {r: 1.0, g: 1.0, b: 1.0},
			// Levels controls
			levelsBlack: 0,
			levelsWhite: 100,
			levelsGamma: 1.0,
			// Curves controls
			curvePower: 1.0,
			curveMidpoint: 50,
			canvasSize: {width: 250, height: 250},
			draggingPoint: null,
			hoveredPoint: null
		};
		this.canvasRef = React.createRef();
		this.fileInputRef = React.createRef();
		this.svgFilterId = 'rgb-curves-filter';
		this.mapPostrenderKey = null;
		// Track last display setting ID to detect changes
		this.lastDisplaySettingId = null;
	}

	componentDidMount() {
		this.ensureSVGFilter();
		if (this.state.open) {
			this.attachMapListener();
		}
		// Listen for custom events to open/close overlay
		this.openOverlayListener = () => {
			this.handleOpen();
		};
		this.closeOverlayListener = () => {
			this.handleClose();
		};
		window.addEventListener('openRGBCurvesOverlay', this.openOverlayListener);
		window.addEventListener('closeRGBCurvesOverlay', this.closeOverlayListener);
		
		// Load RGB curves settings from displaySetting if available
		this.loadSettingsFromDisplaySetting();
		
		// Initialize last display setting ID
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (activeMapId) {
			const mapsState = store.getState().mapsStateReducer;
			const mapState = mapsState[activeMapId];
			const displaySetting = mapState?.displaySetting;
			if (displaySetting) {
				this.lastDisplaySettingId = displaySetting.id;
			}
		}
	}
	
	loadSettingsFromDisplaySetting = () => {
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (!activeMapId) return;
		
		const mapsState = store.getState().mapsStateReducer;
		const mapState = mapsState[activeMapId];
		const displaySetting = mapState?.displaySetting;
		
		if (displaySetting && displaySetting.rgb_curves_settings) {
			const settings = displaySetting.rgb_curves_settings;
			
			// Validate and normalize curve points
			if (settings.curves && settings.linearScales) {
				const normalizedCurves = {};
				['rgb', 'r', 'g', 'b'].forEach(channel => {
					if (settings.curves[channel]) {
						normalizedCurves[channel] = this.normalizeCurvePoints(settings.curves[channel]);
					} else {
						normalizedCurves[channel] = [{x: 0, y: 0}, {x: 1, y: 1}];
					}
				});
				
				this.setState({
					curves: normalizedCurves,
					linearScales: settings.linearScales || this.state.linearScales,
					levelsBlack: settings.levelsBlack !== undefined ? settings.levelsBlack : this.state.levelsBlack,
					levelsWhite: settings.levelsWhite !== undefined ? settings.levelsWhite : this.state.levelsWhite,
					levelsGamma: settings.levelsGamma !== undefined ? settings.levelsGamma : this.state.levelsGamma,
					curvePower: settings.curvePower !== undefined ? settings.curvePower : this.state.curvePower,
					curveMidpoint: settings.curveMidpoint !== undefined ? settings.curveMidpoint : this.state.curveMidpoint,
					filterEnabled: settings.filterEnabled !== undefined ? settings.filterEnabled : this.state.filterEnabled
				}, () => {
					// Update filter and redraw curve after loading settings
					if (this.state.open) {
						this.updateFilter();
						if (this.canvasRef.current) {
							this.drawCurve();
						}
					}
				});
			}
		}
	}
	
	saveSettingsToBackend = () => {
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (!activeMapId) return;
		
		const mapsState = store.getState().mapsStateReducer;
		const mapState = mapsState[activeMapId];
		const displaySetting = mapState?.displaySetting;
		
		if (!displaySetting || !displaySetting.id) return;
		
		const settings = {
			curves: this.state.curves,
			linearScales: this.state.linearScales,
			levelsBlack: this.state.levelsBlack,
			levelsWhite: this.state.levelsWhite,
			levelsGamma: this.state.levelsGamma,
			curvePower: this.state.curvePower,
			curveMidpoint: this.state.curveMidpoint,
			filterEnabled: this.state.filterEnabled
		};
		
		patch(`/api/display/settings/${displaySetting.id}/`, {
			rgb_curves_settings: settings
		})
			.then(updatedSetting => {
				// Update Redux state with the updated display setting
				store.dispatch(updateMapState(activeMapId, {displaySetting: updatedSetting}));
			})
			.catch(error => {
				error_manager('Failed to save RGB curves settings', error);
			});
	}

	componentDidUpdate(prevProps, prevState) {
		// Check if display setting changed (e.g., switching slides)
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (activeMapId) {
			const mapsState = store.getState().mapsStateReducer;
			const mapState = mapsState[activeMapId];
			const currentDisplaySetting = mapState?.displaySetting;
			
			if (currentDisplaySetting && currentDisplaySetting.id !== this.lastDisplaySettingId) {
				// Display setting changed, reload RGB curves settings
				this.lastDisplaySettingId = currentDisplaySetting.id;
				this.loadSettingsFromDisplaySetting();
			}
		}
		
		if (this.state.open && !prevState.open) {
			this.ensureSVGFilter(); // Ensure filter exists
			this.attachMapListener();
			// Initialize filter with current state values
			setTimeout(() => {
				this.updateFilter();
				if (this.canvasRef.current) {
					this.drawCurve();
				}
			}, 100);
		} else if (!this.state.open && prevState.open) {
			this.detachMapListener();
			this.removeFilter();
		} else if (this.state.open) {
			const settingsChanged = 
				this.state.filterEnabled !== prevState.filterEnabled ||
				this.state.curves !== prevState.curves ||
				this.state.linearScales !== prevState.linearScales ||
				this.state.levelsBlack !== prevState.levelsBlack ||
				this.state.levelsWhite !== prevState.levelsWhite ||
				this.state.levelsGamma !== prevState.levelsGamma ||
				this.state.curvePower !== prevState.curvePower ||
				this.state.curveMidpoint !== prevState.curveMidpoint;
			
			if (settingsChanged) {
				this.updateFilter();
			}
			if (this.canvasRef.current) {
				if (this.state.activeChannel !== prevState.activeChannel ||
					this.state.curves !== prevState.curves ||
					this.state.hoveredPoint !== prevState.hoveredPoint ||
					this.state.draggingPoint !== prevState.draggingPoint) {
					this.drawCurve();
				}
			}
		}
	}

	componentWillUnmount() {
		this.detachMapListener();
		this.removeFilter();
		// Remove event listeners
		if (this.openOverlayListener) {
			window.removeEventListener('openRGBCurvesOverlay', this.openOverlayListener);
		}
		if (this.closeOverlayListener) {
			window.removeEventListener('closeRGBCurvesOverlay', this.closeOverlayListener);
		}
	}

	ensureSVGFilter = () => {
		let svg = document.getElementById('rgb-curves-svg');
		if (!svg) {
			svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
			svg.setAttribute('id', 'rgb-curves-svg');
			svg.setAttribute('style', 'position: absolute; width: 0; height: 0;');
			const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
			const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
			filter.setAttribute('id', this.svgFilterId);
			filter.setAttribute('color-interpolation-filters', 'sRGB');

			// Stage 1: RGB Curves
			const feComponentTransferCurves = document.createElementNS('http://www.w3.org/2000/svg', 'feComponentTransfer');
			feComponentTransferCurves.setAttribute('id', 'curves-transfer');
			const feFuncR = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncR');
			feFuncR.setAttribute('id', 'func-r');
			feFuncR.setAttribute('type', 'table');
			feFuncR.setAttribute('tableValues', '0 1');
			const feFuncG = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncG');
			feFuncG.setAttribute('id', 'func-g');
			feFuncG.setAttribute('type', 'table');
			feFuncG.setAttribute('tableValues', '0 1');
			const feFuncB = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncB');
			feFuncB.setAttribute('id', 'func-b');
			feFuncB.setAttribute('type', 'table');
			feFuncB.setAttribute('tableValues', '0 1');
			feComponentTransferCurves.appendChild(feFuncR);
			feComponentTransferCurves.appendChild(feFuncG);
			feComponentTransferCurves.appendChild(feFuncB);
			filter.appendChild(feComponentTransferCurves);

			// Stage 2: Levels - Linear Mapping (Black/White Points)
			const feComponentTransferLevels = document.createElementNS('http://www.w3.org/2000/svg', 'feComponentTransfer');
			feComponentTransferLevels.setAttribute('id', 'levels-linear');
			const feFuncRLevels = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncR');
			feFuncRLevels.setAttribute('id', 'levels-func-r');
			feFuncRLevels.setAttribute('type', 'linear');
			feFuncRLevels.setAttribute('slope', '1');
			feFuncRLevels.setAttribute('intercept', '0');
			const feFuncGLevels = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncG');
			feFuncGLevels.setAttribute('id', 'levels-func-g');
			feFuncGLevels.setAttribute('type', 'linear');
			feFuncGLevels.setAttribute('slope', '1');
			feFuncGLevels.setAttribute('intercept', '0');
			const feFuncBLevels = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncB');
			feFuncBLevels.setAttribute('id', 'levels-func-b');
			feFuncBLevels.setAttribute('type', 'linear');
			feFuncBLevels.setAttribute('slope', '1');
			feFuncBLevels.setAttribute('intercept', '0');
			feComponentTransferLevels.appendChild(feFuncRLevels);
			feComponentTransferLevels.appendChild(feFuncGLevels);
			feComponentTransferLevels.appendChild(feFuncBLevels);
			filter.appendChild(feComponentTransferLevels);

			// Stage 3: Levels - Gamma
			const feComponentTransferGamma = document.createElementNS('http://www.w3.org/2000/svg', 'feComponentTransfer');
			feComponentTransferGamma.setAttribute('id', 'levels-gamma');
			const feFuncRGamma = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncR');
			feFuncRGamma.setAttribute('id', 'levels-gamma-r');
			feFuncRGamma.setAttribute('type', 'gamma');
			feFuncRGamma.setAttribute('exponent', '1');
			const feFuncGGamma = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncG');
			feFuncGGamma.setAttribute('id', 'levels-gamma-g');
			feFuncGGamma.setAttribute('type', 'gamma');
			feFuncGGamma.setAttribute('exponent', '1');
			const feFuncBGamma = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncB');
			feFuncBGamma.setAttribute('id', 'levels-gamma-b');
			feFuncBGamma.setAttribute('type', 'gamma');
			feFuncBGamma.setAttribute('exponent', '1');
			feComponentTransferGamma.appendChild(feFuncRGamma);
			feComponentTransferGamma.appendChild(feFuncGGamma);
			feComponentTransferGamma.appendChild(feFuncBGamma);
			filter.appendChild(feComponentTransferGamma);

			// Stage 4: Curves (Power Curve)
			const feComponentTransferCurvesPower = document.createElementNS('http://www.w3.org/2000/svg', 'feComponentTransfer');
			feComponentTransferCurvesPower.setAttribute('id', 'curves-power');
			const feFuncRCurves = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncR');
			feFuncRCurves.setAttribute('id', 'curves-power-r');
			feFuncRCurves.setAttribute('type', 'table');
			feFuncRCurves.setAttribute('tableValues', this.generateCurveTable(1.0, 50));
			const feFuncGCurves = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncG');
			feFuncGCurves.setAttribute('id', 'curves-power-g');
			feFuncGCurves.setAttribute('type', 'table');
			feFuncGCurves.setAttribute('tableValues', this.generateCurveTable(1.0, 50));
			const feFuncBCurves = document.createElementNS('http://www.w3.org/2000/svg', 'feFuncB');
			feFuncBCurves.setAttribute('id', 'curves-power-b');
			feFuncBCurves.setAttribute('type', 'table');
			feFuncBCurves.setAttribute('tableValues', this.generateCurveTable(1.0, 50));
			feComponentTransferCurvesPower.appendChild(feFuncRCurves);
			feComponentTransferCurvesPower.appendChild(feFuncGCurves);
			feComponentTransferCurvesPower.appendChild(feFuncBCurves);
			filter.appendChild(feComponentTransferCurvesPower);

			defs.appendChild(filter);
			svg.appendChild(defs);
			document.body.appendChild(svg);
		}
	};

	attachMapListener = () => {
		// Use a polling approach to apply filter on canvas updates
		this.filterInterval = setInterval(() => {
			this.applyFilterToCanvas();
		}, 100);
		this.applyFilterToCanvas();
		
		// Also listen for map render events if possible
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		const mapElement = document.querySelector(`#slide-map-${activeMapId}`);
		if (mapElement) {
			// Try to find map instance via redux or element
			const mapsState = store.getState().mapsStateReducer;
			const mapState = mapsState[activeMapId];
			if (mapState && mapState.slidemap) {
				const map = mapState.slidemap;
				if (map && typeof map.on === 'function') {
					this.mapPostrenderKey = map.on('postrender', this.applyFilterToCanvas);
				}
			}
		}
	};

	detachMapListener = () => {
		if (this.filterInterval) {
			clearInterval(this.filterInterval);
			this.filterInterval = null;
		}
		if (this.mapPostrenderKey) {
			const activeMapId = store.getState().gammaStateReducer.activeMapId;
			const mapsState = store.getState().mapsStateReducer;
			const mapState = mapsState[activeMapId];
			if (mapState && mapState.slidemap) {
				const map = mapState.slidemap;
				if (map && typeof map.unByKey === 'function') {
					map.unByKey(this.mapPostrenderKey);
				}
			}
			this.mapPostrenderKey = null;
		}
	};

	applyFilterToCanvas = () => {
		if (!this.state.open) return;
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (!activeMapId) return;
		const canvas = document.querySelector(`#slide-map-${activeMapId} canvas`);
		if (canvas) {
			if (this.state.filterEnabled) {
				canvas.style.filter = `url(#${this.svgFilterId})`;
			} else {
				canvas.style.filter = '';
			}
		}
	};

	updateFilter = () => {
		if (!this.state.filterEnabled) {
			this.removeFilter();
			return;
		}

		// Ensure filter exists
		this.ensureSVGFilter();

		// Get SVG element for querying
		const svg = document.getElementById('rgb-curves-svg');
		if (!svg) {
			console.error('SVG filter container not found');
			return;
		}

		const channels = ['r', 'g', 'b'];
		
		// Update RGB Curves
		channels.forEach(channel => {
			const lut = this.getCurveLUT(channel);
			const sampled = this.sampleLUT(lut, 17);
			const tableValues = sampled.join(' ');
			// Try both querySelector and getElementById
			const funcElement = svg.querySelector(`#func-${channel}`) || document.getElementById(`func-${channel}`);
			if (funcElement) {
				funcElement.setAttribute('tableValues', tableValues);
			} else {
				console.warn(`Element func-${channel} not found`);
			}
		});

		// Update Levels - Linear Mapping (Black/White Points)
		const levelsTransfer = this.calculateLevelsTransfer(this.state.levelsBlack, this.state.levelsWhite);
		channels.forEach(channel => {
			const funcElement = svg.querySelector(`#levels-func-${channel}`) || document.getElementById(`levels-func-${channel}`);
			if (funcElement) {
				funcElement.setAttribute('slope', levelsTransfer.slope.toString());
				funcElement.setAttribute('intercept', levelsTransfer.intercept.toString());
			} else {
				console.warn(`Element levels-func-${channel} not found`);
			}
		});

		// Update Levels - Gamma
		channels.forEach(channel => {
			const funcElement = svg.querySelector(`#levels-gamma-${channel}`) || document.getElementById(`levels-gamma-${channel}`);
			if (funcElement) {
				funcElement.setAttribute('exponent', this.state.levelsGamma.toString());
			} else {
				console.warn(`Element levels-gamma-${channel} not found`);
			}
		});

		// Update Curves (Power Curve)
		const curveTable = this.generateCurveTable(this.state.curvePower, this.state.curveMidpoint);
		channels.forEach(channel => {
			const funcElement = svg.querySelector(`#curves-power-${channel}`) || document.getElementById(`curves-power-${channel}`);
			if (funcElement) {
				funcElement.setAttribute('tableValues', curveTable);
			} else {
				console.warn(`Element curves-power-${channel} not found`);
			}
		});

		// Force map repaint if possible
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		const mapsState = store.getState().mapsStateReducer;
		const mapState = mapsState[activeMapId];
		if (mapState && mapState.slidemap) {
			const map = mapState.slidemap;
			if (map && typeof map.render === 'function') {
				map.render();
			}
		}
		// Force filter refresh by removing and reapplying
		setTimeout(() => {
			const canvas = document.querySelector(`#slide-map-${activeMapId} canvas`);
			if (canvas && this.state.filterEnabled) {
				// Remove filter temporarily to force browser to re-evaluate
				canvas.style.filter = '';
				// Force a repaint by accessing offsetHeight
				void canvas.offsetHeight; // Trigger reflow
				// Reapply filter
				canvas.style.filter = `url(#${this.svgFilterId})`;
				// Force another repaint
				if (mapState && mapState.slidemap) {
					const map = mapState.slidemap;
					if (map && typeof map.render === 'function') {
						map.render();
					}
				}
			}
		}, 10);
	};

	removeFilter = () => {
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (!activeMapId) return;
		const canvas = document.querySelector(`#slide-map-${activeMapId} canvas`);
		if (canvas) {
			canvas.style.filter = '';
		}
	};

	cubicSpline = (points, numSamples = 256) => {
		if (points.length < 2) {
			return new Array(numSamples).fill(0).map((_, i) => i / (numSamples - 1));
		}

		// Sort points by x
		const sortedPoints = [...points].sort((a, b) => a.x - b.x);
		
		// Catmull-Rom spline interpolation
		const result = [];
		for (let i = 0; i < numSamples; i++) {
			const t = i / (numSamples - 1);
			result.push(this.interpolateCatmullRom(sortedPoints, t));
		}
		return result;
	};

	interpolateCatmullRom = (points, t) => {
		if (t <= 0) return points[0].y;
		if (t >= 1) return points[points.length - 1].y;

		// Find the segment
		for (let i = 0; i < points.length - 1; i++) {
			if (t >= points[i].x && t <= points[i + 1].x) {
				const t0 = points[Math.max(0, i - 1)].x;
				const p0 = points[Math.max(0, i - 1)].y;
				const t1 = points[i].x;
				const p1 = points[i].y;
				const t2 = points[i + 1].x;
				const p2 = points[i + 1].y;
				const t3 = points[Math.min(points.length - 1, i + 2)].x;
				const p3 = points[Math.min(points.length - 1, i + 2)].y;

				// Normalize t to segment
				const segmentT = (t - t1) / (t2 - t1 || 1);
				return this.catmullRom(segmentT, p0, p1, p2, p3);
			}
		}
		return points[points.length - 1].y;
	};

	catmullRom = (t, p0, p1, p2, p3) => {
		const t2 = t * t;
		const t3 = t2 * t;
		return 0.5 * (
			(2 * p1) +
			(-p0 + p2) * t +
			(2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
			(-p0 + 3 * p1 - 3 * p2 + p3) * t3
		);
	};

	getCurveLUT = (channel) => {
		const baseCurve = this.cubicSpline(this.state.curves[channel]);
		const masterCurve = this.cubicSpline(this.state.curves.rgb);
		const scale = this.state.linearScales[channel];

		// Check if this is an identity curve (straight line from 0,0 to 1,1)
		const isIdentity = (points) => {
			if (points.length !== 2) return false;
			return Math.abs(points[0].x - 0) < 0.001 && 
				   Math.abs(points[0].y - 0) < 0.001 &&
				   Math.abs(points[1].x - 1) < 0.001 && 
				   Math.abs(points[1].y - 1) < 0.001;
		};

		const isBaseIdentity = isIdentity(this.state.curves[channel]);
		const isMasterIdentity = isIdentity(this.state.curves.rgb);
		const isScaleIdentity = Math.abs(scale - 1.0) < 0.001;

		// If everything is identity, return perfect identity
		if (isBaseIdentity && isMasterIdentity && isScaleIdentity) {
			return Array.from({length: 256}, (_, i) => i / 255);
		}

		return baseCurve.map((v, i) => {
			let value = v;
			// Apply master curve
			const masterIndex = Math.floor(value * 255);
			value = masterCurve[Math.max(0, Math.min(255, masterIndex))];
			// Apply linear scale
			value = value * scale;
			return Math.max(0, Math.min(1, value));
		});
	};

	sampleLUT = (lut, numSamples) => {
		const step = (lut.length - 1) / (numSamples - 1);
		const result = [];
		for (let i = 0; i < numSamples; i++) {
			const index = Math.round(i * step);
			result.push(lut[Math.min(index, lut.length - 1)]);
		}
		return result;
	};

	// Levels calculation functions
	calculateLevelsTransfer = (black, white) => {
		const blackNorm = black / 100;
		const whiteNorm = white / 100;
		const range = whiteNorm - blackNorm;
		if (range <= 0) return { slope: 0, intercept: 0 };
		const slope = 1.0 / range;
		const intercept = -blackNorm / range;
		return { slope, intercept };
	};

	// Curves (Power Curve) generation function
	generateCurveTable = (power, midPoint) => {
		const tableSize = 256;
		const table = [];
		let mid = midPoint / 100;
		
		// Handle edge cases
		if (mid <= 0) {
			mid = 0.001;
		}
		if (mid >= 1) {
			mid = 0.999;
		}
		
		for (let i = 0; i <= tableSize; i++) {
			let value = i / tableSize;
			if (value < mid && mid > 0) {
				value = mid * Math.pow(value / mid, power);
			} else if (value >= mid && mid < 1) {
				const range = 1 - mid;
				if (range > 0.001) {
					value = mid + range * Math.pow((value - mid) / range, power);
				} else {
					value = value; // Keep as is if range is too small
				}
			}
			table.push(Math.max(0, Math.min(1, value)));
		}
		return table.join(' ');
	};

	handleOpen = () => {
		this.setState({open: true}, () => {
			// Dispatch event to notify that overlay is open
			window.dispatchEvent(new CustomEvent('rgbCurvesOverlayOpened'));
			// Wait for dialog to render before drawing
			setTimeout(() => {
				if (this.canvasRef.current) {
					this.drawCurve();
				}
			}, 100);
		});
	};

	handleClose = () => {
		this.setState({open: false}, () => {
			// Dispatch event to notify that overlay is closed
			window.dispatchEvent(new CustomEvent('rgbCurvesOverlayClosed'));
		});
	};

	handleChannelChange = (event, newValue) => {
		this.setState({activeChannel: newValue});
		this.drawCurve();
	};

	handleToggleFilter = () => {
		this.setState({filterEnabled: !this.state.filterEnabled});
	};

	handleLinearScaleChange = (channel, value) => {
		this.setState({
			linearScales: {
				...this.state.linearScales,
				[channel]: value
			}
		});
	};

	handleDownloadSettings = () => {
		// Get scan name and URL from Redux state
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		const mapsState = store.getState().mapsStateReducer;
		const mapState = mapsState[activeMapId];
		const slideName = mapState?.slide?.name || 'Unknown';
		const currentUrl = window.location.href;

		const settings = {
			curves: this.state.curves,
			linearScales: this.state.linearScales,
			levelsBlack: this.state.levelsBlack,
			levelsWhite: this.state.levelsWhite,
			levelsGamma: this.state.levelsGamma,
			curvePower: this.state.curvePower,
			curveMidpoint: this.state.curveMidpoint,
			filterEnabled: this.state.filterEnabled,
			version: '1.0',
			timestamp: new Date().toISOString(),
			scanName: slideName,
			url: currentUrl
		};

		const jsonString = JSON.stringify(settings, null, 2);
		const blob = new Blob([jsonString], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `rgb-curves-settings-${new Date().toISOString().split('T')[0]}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	handleImportSettings = () => {
		if (this.fileInputRef.current) {
			this.fileInputRef.current.click();
		}
	};

	normalizeCurvePoints = (points) => {
		if (!Array.isArray(points) || points.length === 0) {
			return [{x: 0, y: 0}, {x: 1, y: 1}];
		}

		// Validate and clamp points to [0, 1] range
		const validPoints = points.map(p => ({
			x: Math.max(0, Math.min(1, p.x || 0)),
			y: Math.max(0, Math.min(1, p.y || 0))
		}));

		// Sort by x-coordinate
		const sorted = [...validPoints].sort((a, b) => a.x - b.x);

		// Remove duplicate points (same x value, keep the one with closest y to average)
		const deduplicated = [];
		for (let i = 0; i < sorted.length; i++) {
			if (i === 0 || Math.abs(sorted[i].x - sorted[i - 1].x) > 0.0001) {
				deduplicated.push({...sorted[i]});
			} else {
				// If x values are very close, merge them by averaging y
				const last = deduplicated[deduplicated.length - 1];
				last.y = (last.y + sorted[i].y) / 2;
			}
		}

		// Ensure first point is at x=0
		if (deduplicated.length > 0) {
			deduplicated[0].x = 0;
		} else {
			deduplicated.push({x: 0, y: 0});
		}

		// Ensure last point is at x=1
		if (deduplicated.length > 1) {
			deduplicated[deduplicated.length - 1].x = 1;
		} else {
			deduplicated.push({x: 1, y: 1});
		}

		// Ensure we have at least 2 points
		if (deduplicated.length < 2) {
			return [{x: 0, y: 0}, {x: 1, y: 1}];
		}

		return deduplicated;
	};

	handleFileChange = (event) => {
		const file = event.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const settings = JSON.parse(e.target.result);
				
				// Validate and apply settings
				if (settings.curves && settings.linearScales) {
					// Normalize curve points for all channels
					const normalizedCurves = {};
					['rgb', 'r', 'g', 'b'].forEach(channel => {
						if (settings.curves[channel]) {
							normalizedCurves[channel] = this.normalizeCurvePoints(settings.curves[channel]);
						} else {
							normalizedCurves[channel] = [{x: 0, y: 0}, {x: 1, y: 1}];
						}
					});

					this.setState({
						curves: normalizedCurves,
						linearScales: settings.linearScales || this.state.linearScales,
						levelsBlack: settings.levelsBlack !== undefined ? settings.levelsBlack : this.state.levelsBlack,
						levelsWhite: settings.levelsWhite !== undefined ? settings.levelsWhite : this.state.levelsWhite,
						levelsGamma: settings.levelsGamma !== undefined ? settings.levelsGamma : this.state.levelsGamma,
						curvePower: settings.curvePower !== undefined ? settings.curvePower : this.state.curvePower,
						curveMidpoint: settings.curveMidpoint !== undefined ? settings.curveMidpoint : this.state.curveMidpoint,
						filterEnabled: settings.filterEnabled !== undefined ? settings.filterEnabled : this.state.filterEnabled
					}, () => {
						// Update filter after state is set
						this.updateFilter();
						// Redraw curve canvas
						if (this.canvasRef.current) {
							this.drawCurve();
						}
					});
				} else {
					console.error('Invalid settings file format');
					alert('Invalid settings file. Please select a valid RGB Curves settings file.');
				}
			} catch (error) {
				console.error('Error parsing settings file:', error);
				alert('Error reading settings file. Please ensure it is a valid JSON file.');
			}
		};
		reader.readAsText(file);
		
		// Reset file input so same file can be selected again
		event.target.value = '';
	};

	handleReset = () => {
		// Reset to identity curves (straight diagonal line)
		const identityCurve = [{x: 0, y: 0}, {x: 1, y: 1}];
		
		// First, completely remove the filter from canvas
		const activeMapId = store.getState().gammaStateReducer.activeMapId;
		if (activeMapId) {
			const canvas = document.querySelector(`#slide-map-${activeMapId} canvas`);
			if (canvas) {
				canvas.style.filter = '';
			}
		}
		
		// Update SVG filter with perfect identity values (0 to 1 linear)
		const identityValues = Array.from({length: 17}, (_, i) => i / 16).join(' ');
		const svg = document.getElementById('rgb-curves-svg');
		if (svg) {
			['r', 'g', 'b'].forEach(channel => {
				const funcElement = svg.querySelector(`#func-${channel}`) || document.getElementById(`func-${channel}`);
				if (funcElement) {
					funcElement.setAttribute('tableValues', identityValues);
				}
				// Reset Levels to identity
				const levelsFunc = svg.querySelector(`#levels-func-${channel}`) || document.getElementById(`levels-func-${channel}`);
				if (levelsFunc) {
					levelsFunc.setAttribute('slope', '1');
					levelsFunc.setAttribute('intercept', '0');
				}
				const gammaFunc = svg.querySelector(`#levels-gamma-${channel}`) || document.getElementById(`levels-gamma-${channel}`);
				if (gammaFunc) {
					gammaFunc.setAttribute('exponent', '1');
				}
				// Reset Curves to identity
				const curvesFunc = svg.querySelector(`#curves-power-${channel}`) || document.getElementById(`curves-power-${channel}`);
				if (curvesFunc) {
					const identityCurveTable = this.generateCurveTable(1.0, 50);
					curvesFunc.setAttribute('tableValues', identityCurveTable);
				}
			});
		}
		
		// Reset state
		this.setState({
			curves: {
				rgb: JSON.parse(JSON.stringify(identityCurve)), // Deep copy
				r: JSON.parse(JSON.stringify(identityCurve)),
				g: JSON.parse(JSON.stringify(identityCurve)),
				b: JSON.parse(JSON.stringify(identityCurve))
			},
			linearScales: {r: 1.0, g: 1.0, b: 1.0},
			levelsBlack: 0,
			levelsWhite: 100,
			levelsGamma: 1.0,
			curvePower: 1.0,
			curveMidpoint: 50
		}, () => {
			// Double-check SVG filter is set to identity
			const identityValues = Array.from({length: 17}, (_, i) => i / 16).join(' ');
			['r', 'g', 'b'].forEach(channel => {
				const funcElement = document.getElementById(`func-${channel}`);
				if (funcElement) {
					funcElement.setAttribute('tableValues', identityValues);
				}
			});
			
			// Redraw curve to show identity line
			if (this.canvasRef.current) {
				this.drawCurve();
			}
			
			// Force map repaint to ensure filter is cleared
			setTimeout(() => {
				const activeMapId = store.getState().gammaStateReducer.activeMapId;
				const mapsState = store.getState().mapsStateReducer;
				const mapState = mapsState[activeMapId];
				if (mapState && mapState.slidemap) {
					const map = mapState.slidemap;
					if (map && typeof map.render === 'function') {
						map.render();
					}
				}
				// Ensure filter is removed
				const canvas = document.querySelector(`#slide-map-${activeMapId} canvas`);
				if (canvas) {
					canvas.style.filter = '';
				}
			}, 100);
		});
	};

	applyPreset = (preset) => {
		let curves = {...this.state.curves};
		switch (preset) {
			case 'contrast':
				curves.rgb = [{x: 0, y: 0}, {x: 0.5, y: 0.3}, {x: 1, y: 1}];
				break;
			case 'brighten':
				curves.rgb = [{x: 0, y: 0.2}, {x: 1, y: 1}];
				break;
			case 'darken':
				curves.rgb = [{x: 0, y: 0}, {x: 1, y: 0.8}];
				break;
			case 'fade':
				curves.rgb = [{x: 0, y: 0.1}, {x: 1, y: 0.9}];
				break;
			case 'invert':
				curves.rgb = [{x: 0, y: 1}, {x: 1, y: 0}];
				break;
			default:
				return;
		}
		this.setState({curves}, () => {
			this.drawCurve();
			this.updateFilter();
		});
	};

	canvasToCurvePoint = (canvasX, canvasY) => {
		const rect = this.canvasRef.current.getBoundingClientRect();
		const padding = 20;
		const x = (canvasX - rect.left - padding) / (rect.width - 2 * padding);
		const y = 1 - (canvasY - rect.top - padding) / (rect.height - 2 * padding);
		return {
			x: Math.max(0, Math.min(1, x)),
			y: Math.max(0, Math.min(1, y))
		};
	};

	curvePointToCanvas = (point) => {
		const padding = 20;
		return {
			x: padding + point.x * (this.state.canvasSize.width - 2 * padding),
			y: padding + (1 - point.y) * (this.state.canvasSize.height - 2 * padding)
		};
	};

	drawCurve = () => {
		const canvas = this.canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext('2d');
		const {width, height} = this.state.canvasSize;
		const padding = 20;

		// Clear with theme color matching top bar
		ctx.fillStyle = 'rgba(51, 4, 112, 0.5)'; // Top bar color with transparency
		ctx.fillRect(0, 0, width, height);

		// Draw grid
		ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
		ctx.lineWidth = 1;
		for (let i = 0; i <= 4; i++) {
			const pos = padding + (i / 4) * (width - 2 * padding);
			// Vertical lines
			ctx.beginPath();
			ctx.moveTo(pos, padding);
			ctx.lineTo(pos, height - padding);
			ctx.stroke();
			// Horizontal lines
			ctx.beginPath();
			ctx.moveTo(padding, pos);
			ctx.lineTo(width - padding, pos);
			ctx.stroke();
		}

		// Draw diagonal identity line
		ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
		ctx.lineWidth = 1.5;
		ctx.setLineDash([5, 5]);
		ctx.beginPath();
		ctx.moveTo(padding, height - padding);
		ctx.lineTo(width - padding, padding);
		ctx.stroke();
		ctx.setLineDash([]);

		// Draw curve
		const channel = this.state.activeChannel;
		const points = this.state.curves[channel];
		const sortedPoints = [...points].sort((a, b) => a.x - b.x);
		
		// Use brighter, more visible colors
		ctx.strokeStyle = channel === 'rgb' ? '#fff' : 
			channel === 'r' ? '#ff6b6b' : 
			channel === 'g' ? '#51cf66' : '#4dabf7';
		ctx.lineWidth = 3;
		ctx.shadowBlur = 4;
		ctx.shadowColor = channel === 'rgb' ? 'rgba(255, 255, 255, 0.5)' : 
			channel === 'r' ? 'rgba(255, 107, 107, 0.5)' : 
			channel === 'g' ? 'rgba(81, 207, 102, 0.5)' : 'rgba(77, 171, 247, 0.5)';

		// Draw interpolated curve
		const lut = this.cubicSpline(sortedPoints, 200);
		ctx.beginPath();
		for (let i = 0; i < lut.length; i++) {
			const x = padding + (i / (lut.length - 1)) * (width - 2 * padding);
			const y = padding + (1 - lut[i]) * (height - 2 * padding);
			if (i === 0) ctx.moveTo(x, y);
			else ctx.lineTo(x, y);
		}
		ctx.stroke();
		ctx.shadowBlur = 0;

		// Draw control points
		sortedPoints.forEach((point, index) => {
			const pos = this.curvePointToCanvas(point);
			const isFirst = index === 0;
			const isLast = index === sortedPoints.length - 1;
			const isHovered = this.state.hoveredPoint === index;
			
			// Use brighter colors for points
			ctx.fillStyle = isHovered ? '#ffd43b' : 
				channel === 'rgb' ? '#fff' : 
				channel === 'r' ? '#ff6b6b' : 
				channel === 'g' ? '#51cf66' : '#4dabf7';
			ctx.beginPath();
			ctx.arc(pos.x, pos.y, isHovered ? 7 : 5, 0, 2 * Math.PI);
			ctx.fill();
			
			// White border for visibility
			ctx.strokeStyle = '#fff';
			ctx.lineWidth = 2;
			ctx.stroke();
		});
	};

	handleCanvasMouseDown = (e) => {
		const point = this.canvasToCurvePoint(e.clientX, e.clientY);
		const channel = this.state.activeChannel;
		const points = this.state.curves[channel];
		const sortedPoints = [...points].sort((a, b) => a.x - b.x);

		// Check if clicking on existing point
		for (let i = 0; i < sortedPoints.length; i++) {
			const canvasPos = this.curvePointToCanvas(sortedPoints[i]);
			const dx = e.clientX - (this.canvasRef.current.getBoundingClientRect().left + canvasPos.x);
			const dy = e.clientY - (this.canvasRef.current.getBoundingClientRect().top + canvasPos.y);
			if (Math.sqrt(dx * dx + dy * dy) < 10) {
				this.setState({draggingPoint: i});
				return;
			}
		}

		// Store the original last point before adding new point
		const originalLastPoint = sortedPoints.length > 0 ? {...sortedPoints[sortedPoints.length - 1]} : null;
		
		// Constrain new point: don't allow x=1 (reserved for last point) or x=0 (reserved for first point)
		if (point.x >= 0.99) {
			point.x = 0.99;
		}
		if (point.x <= 0.01) {
			point.x = 0.01;
		}
		
		// Add new point and sort (excluding the last point from sorting)
		const middlePoints = sortedPoints.slice(1, -1); // Get points between first and last
		const newMiddlePoints = [...middlePoints, point].sort((a, b) => a.x - b.x);
		
		// Reconstruct array: first point (x=0), middle points, last point (x=1)
		const firstPoint = sortedPoints.length > 0 ? {...sortedPoints[0], x: 0} : {x: 0, y: 0};
		const lastPoint = originalLastPoint ? {...originalLastPoint, x: 1} : {x: 1, y: 1};
		const newPoints = [firstPoint, ...newMiddlePoints, lastPoint];
		
		// Find the index of the newly added point in the sorted array
		const newPointIndex = newPoints.findIndex((p, idx) => 
			idx > 0 && idx < newPoints.length - 1 && 
			Math.abs(p.x - point.x) < 0.01 && Math.abs(p.y - point.y) < 0.01
		);
		
		this.setState({
			curves: {
				...this.state.curves,
				[channel]: newPoints
			},
			draggingPoint: newPointIndex >= 0 ? newPointIndex : (newPoints.length > 2 ? newPoints.length - 2 : 1)
		}, () => {
			this.drawCurve();
			this.updateFilter();
		});
	};

	handleCanvasMouseMove = (e) => {
		if (this.state.draggingPoint !== null) {
			const point = this.canvasToCurvePoint(e.clientX, e.clientY);
			const channel = this.state.activeChannel;
			const points = [...this.state.curves[channel]];
			const sortedPoints = [...points].sort((a, b) => a.x - b.x);
			const draggingIndex = this.state.draggingPoint;

			// Constrain first/last points
			if (draggingIndex === 0) {
				point.x = 0;
			} else if (draggingIndex === sortedPoints.length - 1) {
				point.x = 1;
			} else {
				// Constrain x for middle points to stay between adjacent points
				if (draggingIndex > 0) {
					point.x = Math.max(sortedPoints[draggingIndex - 1].x + 0.001, point.x);
				}
				if (draggingIndex < sortedPoints.length - 1) {
					point.x = Math.min(sortedPoints[draggingIndex + 1].x - 0.001, point.x);
				}
			}

			// Update the point in the sorted array
			sortedPoints[draggingIndex] = point;
			
			// Re-sort to maintain order
			const reSortedPoints = [...sortedPoints].sort((a, b) => a.x - b.x);
			
			// Ensure first and last points maintain their x positions
			if (reSortedPoints.length > 0) {
				reSortedPoints[0].x = 0;
			}
			if (reSortedPoints.length > 1) {
				reSortedPoints[reSortedPoints.length - 1].x = 1;
			}
			
			// Find the new index of the dragged point after re-sorting
			const newDraggingIndex = reSortedPoints.findIndex((p, idx) => 
				idx === draggingIndex || (Math.abs(p.x - point.x) < 0.001 && Math.abs(p.y - point.y) < 0.001)
			);

			this.setState({
				curves: {
					...this.state.curves,
					[channel]: reSortedPoints
				},
				draggingPoint: newDraggingIndex >= 0 ? newDraggingIndex : draggingIndex
			}, () => {
				this.drawCurve();
				this.updateFilter();
			});
		} else {
			// Check hover
			const channel = this.state.activeChannel;
			const points = this.state.curves[channel];
			const sortedPoints = [...points].sort((a, b) => a.x - b.x);
			let hovered = null;

			for (let i = 0; i < sortedPoints.length; i++) {
				const canvasPos = this.curvePointToCanvas(sortedPoints[i]);
				const dx = e.clientX - (this.canvasRef.current.getBoundingClientRect().left + canvasPos.x);
				const dy = e.clientY - (this.canvasRef.current.getBoundingClientRect().top + canvasPos.y);
				if (Math.sqrt(dx * dx + dy * dy) < 10) {
					hovered = i;
					break;
				}
			}

			if (hovered !== this.state.hoveredPoint) {
				this.setState({hoveredPoint: hovered}, () => this.drawCurve());
			}
		}
	};

	handleCanvasMouseUp = () => {
		this.setState({draggingPoint: null});
	};

	handleCanvasDoubleClick = (e) => {
		const channel = this.state.activeChannel;
		const points = [...this.state.curves[channel]];
		const sortedPoints = [...points].sort((a, b) => a.x - b.x);

		// Find closest point
		let closestIndex = -1;
		let minDist = Infinity;
		for (let i = 0; i < sortedPoints.length; i++) {
			const canvasPos = this.curvePointToCanvas(sortedPoints[i]);
			const dx = e.clientX - (this.canvasRef.current.getBoundingClientRect().left + canvasPos.x);
			const dy = e.clientY - (this.canvasRef.current.getBoundingClientRect().top + canvasPos.y);
			const dist = Math.sqrt(dx * dx + dy * dy);
			if (dist < 10 && dist < minDist) {
				minDist = dist;
				closestIndex = i;
			}
		}

		// Remove point (except first/last)
		if (closestIndex > 0 && closestIndex < sortedPoints.length - 1) {
			const actualIndex = points.indexOf(sortedPoints[closestIndex]);
			points.splice(actualIndex, 1);
			this.setState({
				curves: {
					...this.state.curves,
					[channel]: points
				}
			}, () => {
				this.drawCurve();
				this.updateFilter();
			});
		}
	};

	render() {
		const headBarHeight = 48; // Height of the top bar
		
		return (
			<Drawer
				anchor="right"
				open={this.state.open}
				onClose={this.handleClose}
				ModalProps={{
					// Make backdrop non-blocking so clicks pass through to map
					style: { pointerEvents: 'none' }
				}}
				PaperProps={{
					style: {
						backgroundColor: 'rgba(51, 4, 112, 0.3)', // Top bar color (#330470) with 30% transparency
						color: '#fff',
						width: '400px',
						top: `${headBarHeight}px`, // Position below headbar
						height: `calc(100% - ${headBarHeight}px)`, // Full height minus headbar
						// Ensure drawer content can receive pointer events
						pointerEvents: 'auto',
						zIndex: 1200, // Below headbar but above map
						backdropFilter: 'blur(10px)' // Optional: adds blur effect for better readability
					}
				}}
				sx={{
					'& .MuiBackdrop-root': {
						// Make backdrop invisible and non-blocking
						backgroundColor: 'transparent',
						pointerEvents: 'none',
						top: `${headBarHeight}px`,
						height: `calc(100% - ${headBarHeight}px)`
					}
				}}
			>
				<Box sx={{padding: 1.5, height: '100%', overflow: 'auto', overflowX: 'hidden'}}>
					{/* Header */}
					<Box sx={{
						display: 'flex', 
						justifyContent: 'space-between', 
						alignItems: 'center', 
						marginBottom: 1,
						paddingBottom: 0.5,
						borderBottom: '1px solid rgba(255, 255, 255, 0.3)'
					}}>
						<Typography variant="body1" sx={{color: '#fff', fontWeight: 'bold', fontSize: '1rem'}}>RGB Curves</Typography>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
							<input
								type="file"
								ref={this.fileInputRef}
								onChange={this.handleFileChange}
								accept=".json"
								style={{ display: 'none' }}
							/>
							<Tooltip title="Import Settings" placement="bottom">
								<IconButton 
									onClick={this.handleImportSettings}
									size="small"
									sx={{
										backgroundColor: 'rgba(255, 255, 255, 0.25)',
										color: '#fff',
										border: '1px solid rgba(255, 255, 255, 0.5)',
										padding: '4px',
										'&:hover': {
											backgroundColor: 'rgba(255, 255, 255, 0.35)',
											border: '1px solid rgba(255, 255, 255, 0.7)'
										}
									}}
								>
									<UploadIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Download Settings" placement="bottom">
								<IconButton 
									onClick={this.handleDownloadSettings}
									size="small"
									sx={{
										backgroundColor: 'rgba(255, 255, 255, 0.25)',
										color: '#fff',
										border: '1px solid rgba(255, 255, 255, 0.5)',
										padding: '4px',
										'&:hover': {
											backgroundColor: 'rgba(255, 255, 255, 0.35)',
											border: '1px solid rgba(255, 255, 255, 0.7)'
										}
									}}
								>
									<DownloadIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Save Settings" placement="bottom">
								<IconButton 
									onClick={this.saveSettingsToBackend}
									size="small"
									sx={{
										backgroundColor: 'rgba(76, 175, 80, 0.7)',
										color: '#fff',
										border: '1px solid rgba(76, 175, 80, 0.9)',
										padding: '4px',
										'&:hover': {
											backgroundColor: 'rgba(76, 175, 80, 0.9)',
											border: '1px solid rgba(76, 175, 80, 1)'
										}
									}}
								>
									<SaveIcon />
								</IconButton>
							</Tooltip>
							<Button 
								variant="contained" 
								color="secondary" 
								onClick={this.handleReset}
								size="small"
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.25)',
									color: '#fff',
									border: '1px solid rgba(255, 255, 255, 0.5)',
									padding: '4px 8px',
									fontSize: '0.7rem',
									fontWeight: 'bold',
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.35)',
										border: '1px solid rgba(255, 255, 255, 0.7)'
									}
								}}
							>
								Reset
							</Button>
							<Tooltip title={this.state.filterEnabled ? "Hide filter" : "Show filter"}>
								<IconButton 
									onClick={this.handleToggleFilter} 
									size="small" 
									sx={{
										color: '#fff', 
										marginRight: 0.5,
										'&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
									}}
								>
									{this.state.filterEnabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
								</IconButton>
							</Tooltip>
							<IconButton 
								onClick={this.handleClose} 
								size="small" 
								sx={{
									color: '#fff',
									'&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
								}}
							>
								<CloseIcon />
							</IconButton>
						</Box>
					</Box>

					{/* Channel Tabs */}
					<Tabs
						value={this.state.activeChannel}
						onChange={this.handleChannelChange}
						textColor="inherit"
						indicatorColor="secondary"
						sx={{
							borderBottom: 1, 
							borderColor: 'rgba(255, 255, 255, 0.3)', 
							marginBottom: 1,
							minHeight: '36px',
							'& .MuiTab-root': {
								color: 'rgba(255, 255, 255, 0.7)',
								minHeight: '36px',
								padding: '6px 12px',
								fontSize: '0.75rem',
								'&.Mui-selected': {
									color: '#fff',
									fontWeight: 'bold'
								}
							}
						}}
					>
						<Tab label="RGB" value="rgb" />
						<Tab label="R" value="r" />
						<Tab label="G" value="g" />
						<Tab label="B" value="b" />
					</Tabs>

					{/* Curve Canvas */}
					<Box sx={{marginBottom: 1, textAlign: 'center'}}>
						<canvas
							ref={this.canvasRef}
							width={this.state.canvasSize.width}
							height={this.state.canvasSize.height}
							onMouseDown={this.handleCanvasMouseDown}
							onMouseMove={this.handleCanvasMouseMove}
							onMouseUp={this.handleCanvasMouseUp}
							onDoubleClick={this.handleCanvasDoubleClick}
							onMouseLeave={this.handleCanvasMouseUp}
							style={{
								cursor: 'crosshair',
								border: '2px solid rgba(255, 255, 255, 0.5)',
								display: 'block',
								margin: '0 auto',
								borderRadius: '4px'
							}}
						/>
					</Box>

					{/* Presets */}
					<Box sx={{marginBottom: 1}}>
						<Typography variant="body2" sx={{marginBottom: 0.5, color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
							Presets
						</Typography>
						<Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
							<Button 
								variant="contained" 
								size="small" 
								onClick={() => this.applyPreset('contrast')}
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
									color: '#fff',
									border: '1px solid rgba(255, 255, 255, 0.4)',
									padding: '4px 8px',
									fontSize: '0.7rem',
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.3)',
										border: '1px solid rgba(255, 255, 255, 0.6)'
									}
								}}
							>
								Contrast
							</Button>
							<Button 
								variant="contained" 
								size="small" 
								onClick={() => this.applyPreset('brighten')}
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
									color: '#fff',
									border: '1px solid rgba(255, 255, 255, 0.4)',
									padding: '4px 8px',
									fontSize: '0.7rem',
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.3)',
										border: '1px solid rgba(255, 255, 255, 0.6)'
									}
								}}
							>
								Brighten
							</Button>
							<Button 
								variant="contained" 
								size="small" 
								onClick={() => this.applyPreset('darken')}
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
									color: '#fff',
									border: '1px solid rgba(255, 255, 255, 0.4)',
									padding: '4px 8px',
									fontSize: '0.7rem',
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.3)',
										border: '1px solid rgba(255, 255, 255, 0.6)'
									}
								}}
							>
								Darken
							</Button>
							<Button 
								variant="contained" 
								size="small" 
								onClick={() => this.applyPreset('fade')}
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
									color: '#fff',
									border: '1px solid rgba(255, 255, 255, 0.4)',
									padding: '4px 8px',
									fontSize: '0.7rem',
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.3)',
										border: '1px solid rgba(255, 255, 255, 0.6)'
									}
								}}
							>
								Fade
							</Button>
							<Button 
								variant="contained" 
								size="small" 
								onClick={() => this.applyPreset('invert')}
								sx={{
									backgroundColor: 'rgba(255, 255, 255, 0.2)',
									color: '#fff',
									border: '1px solid rgba(255, 255, 255, 0.4)',
									padding: '4px 8px',
									fontSize: '0.7rem',
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'rgba(255, 255, 255, 0.3)',
										border: '1px solid rgba(255, 255, 255, 0.6)'
									}
								}}
							>
								Invert
							</Button>
						</Box>
					</Box>

					{/* Linear Scales */}
					<Box sx={{marginBottom: 1}}>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								R Scale: {this.state.linearScales.r.toFixed(2)}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.linearScales.r}
							onChange={(e, value) => this.handleLinearScaleChange('r', value)}
							min={0}
							max={2}
							step={0.01}
							sx={{
								color: '#ff6b6b',
								'& .MuiSlider-thumb': {
									backgroundColor: '#ff6b6b',
									border: '2px solid #fff',
									boxShadow: '0 0 0 2px rgba(255, 107, 107, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#ff6b6b'
								}
							}}
								/>
							</Box>
						</Box>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginTop: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								G Scale: {this.state.linearScales.g.toFixed(2)}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.linearScales.g}
							onChange={(e, value) => this.handleLinearScaleChange('g', value)}
							min={0}
							max={2}
							step={0.01}
							sx={{
								color: '#51cf66',
								'& .MuiSlider-thumb': {
									backgroundColor: '#51cf66',
									border: '2px solid #fff',
									boxShadow: '0 0 0 2px rgba(81, 207, 102, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#51cf66'
								}
							}}
								/>
							</Box>
						</Box>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginTop: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								B Scale: {this.state.linearScales.b.toFixed(2)}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.linearScales.b}
							onChange={(e, value) => this.handleLinearScaleChange('b', value)}
							min={0}
							max={2}
							step={0.01}
							sx={{
								color: '#4dabf7',
								'& .MuiSlider-thumb': {
									backgroundColor: '#4dabf7',
									border: '2px solid #fff',
									boxShadow: '0 0 0 2px rgba(77, 171, 247, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#4dabf7'
								}
							}}
								/>
							</Box>
						</Box>
					</Box>

					{/* Levels Controls */}
					<Box sx={{marginBottom: 1, marginTop: 1}}>
						<Typography variant="body2" sx={{color: '#fff', fontWeight: 'bold', marginBottom: 0.5, fontSize: '0.8rem'}}>
							Levels
						</Typography>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								Black Point: {this.state.levelsBlack}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.levelsBlack}
							onChange={(e, value) => this.setState({levelsBlack: value}, () => this.updateFilter())}
							min={0}
							max={100}
							step={1}
							sx={{
								color: '#fff',
								'& .MuiSlider-thumb': {
									backgroundColor: '#fff',
									border: '2px solid #330470',
									boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#fff'
								}
							}}
								/>
							</Box>
						</Box>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginTop: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								White Point: {this.state.levelsWhite}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.levelsWhite}
							onChange={(e, value) => this.setState({levelsWhite: value}, () => this.updateFilter())}
							min={0}
							max={100}
							step={1}
							sx={{
								color: '#fff',
								'& .MuiSlider-thumb': {
									backgroundColor: '#fff',
									border: '2px solid #330470',
									boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#fff'
								}
							}}
								/>
							</Box>
						</Box>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginTop: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								Levels Gamma: {this.state.levelsGamma.toFixed(2)}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.levelsGamma}
							onChange={(e, value) => this.setState({levelsGamma: value}, () => this.updateFilter())}
							min={0.1}
							max={3.0}
							step={0.01}
							sx={{
								color: '#fff',
								'& .MuiSlider-thumb': {
									backgroundColor: '#fff',
									border: '2px solid #330470',
									boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#fff'
								}
							}}
								/>
							</Box>
						</Box>
					</Box>

					{/* Curves Controls */}
					<Box sx={{marginBottom: 1}}>
						<Typography variant="body2" sx={{color: '#fff', fontWeight: 'bold', marginBottom: 0.5, fontSize: '0.8rem'}}>
							Curves
						</Typography>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								Curve Power: {this.state.curvePower.toFixed(2)}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.curvePower}
							onChange={(e, value) => this.setState({curvePower: value}, () => this.updateFilter())}
							min={0.1}
							max={5.0}
							step={0.01}
							sx={{
								color: '#fff',
								'& .MuiSlider-thumb': {
									backgroundColor: '#fff',
									border: '2px solid #330470',
									boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#fff'
								}
							}}
								/>
							</Box>
						</Box>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1, marginTop: 1}}>
							<Typography variant="body2" sx={{width: '40%', color: '#fff', fontWeight: 'bold', fontSize: '0.75rem'}}>
								Curve Midpoint: {this.state.curveMidpoint}
							</Typography>
							<Box sx={{width: '60%'}}>
								<Slider
							value={this.state.curveMidpoint}
							onChange={(e, value) => this.setState({curveMidpoint: value}, () => this.updateFilter())}
							min={0}
							max={100}
							step={1}
							sx={{
								color: '#fff',
								'& .MuiSlider-thumb': {
									backgroundColor: '#fff',
									border: '2px solid #330470',
									boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.3)'
								},
								'& .MuiSlider-track': {
									backgroundColor: '#fff'
								}
							}}
								/>
							</Box>
						</Box>
					</Box>
				</Box>
			</Drawer>
		);
	}
}

export default RGBCurvesOverlay;

